<template>
  <div class="test-scores-tab" v-loading="loading">
    <!-- 测试统计卡片 -->
    <el-row :gutter="20" class="summary-row">
      <el-col :span="6">
        <div class="summary-card">
          <div class="summary-icon" style="background: #ecf5ff; color: #409eff">
            <i class="el-icon-document"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ testStats.totalTests || 0 }}</div>
            <div class="summary-label">测试总次数</div>
          </div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="summary-card">
          <div class="summary-icon" style="background: #f0f9ff; color: #67c23a">
            <i class="el-icon-medal"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ testStats.avgScore || 0 }}%</div>
            <div class="summary-label">平均分数</div>
          </div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="summary-card">
          <div class="summary-icon" style="background: #fef0f0; color: #f56c6c">
            <i class="el-icon-trophy"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ testStats.highestScore || 0 }}%</div>
            <div class="summary-label">最高分数</div>
          </div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="summary-card">
          <div class="summary-icon" style="background: #fdf6ec; color: #e6a23c">
            <i class="el-icon-data-line"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ testStats.passRate || 0 }}%</div>
            <div class="summary-label">及格率</div>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 测试成绩趋势图 -->
    <el-card class="chart-card" shadow="never">
      <div slot="header" class="card-header">
        <span>测试成绩趋势</span>
        <el-radio-group v-model="chartPeriod" size="small" @change="handlePeriodChange">
          <el-radio-button label="all">全部</el-radio-button>
          <el-radio-button label="30">最近30天</el-radio-button>
          <el-radio-button label="90">最近90天</el-radio-button>
        </el-radio-group>
      </div>
      <div ref="trendChart" class="chart-container"></div>
    </el-card>

    <!-- 成绩分布图 -->
    <el-card class="chart-card" shadow="never">
      <div slot="header" class="card-header">
        <span>成绩分布</span>
      </div>
      <div ref="distributionChart" class="chart-container" style="height: 300px"></div>
    </el-card>

    <!-- 测试记录列表 -->
    <el-card class="detail-card" shadow="never">
      <div slot="header" class="card-header">
        <span>测试记录</span>
        <!-- <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAddTest">
          新建测试
        </el-button> -->
      </div>
      <el-table
        :data="testRecords"
        style="width: 100%"
        max-height="500"
      >
        <el-table-column type="index" label="序号" width="60" />
        <el-table-column prop="testName" label="测试名称" min-width="180" />
        <el-table-column prop="testType" label="测试类型" width="100">
          <template slot-scope="scope">
            <el-tag :type="testTypeTag(scope.row.testType)" size="small">
              {{ testTypeText(scope.row.testType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="totalQuestions" label="题目数量" width="100" align="center" />
        <el-table-column prop="correctCount" label="正确数量" width="100" align="center">
          <template slot-scope="scope">
            <span class="success-text">{{ scope.row.correctCount }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="score" label="得分" width="100" align="center">
          <template slot-scope="scope">
            <span :class="scoreClass(scope.row.score)">{{ scope.row.score }}%</span>
          </template>
        </el-table-column>
        <el-table-column prop="duration" label="用时" width="100" align="center">
          <template slot-scope="scope">
            <span>{{ formatDuration(scope.row.duration) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="testDate" label="测试时间" min-width="160" />
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="text"
              icon="el-icon-view"
              size="small"
              @click="handleViewDetail(scope.row)"
            >
              详情
            </el-button>
            <el-button
              type="text"
              icon="el-icon-delete"
              size="small"
              style="color: #f56c6c"
              @click="handleDelete(scope.row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 测试详情对话框 -->
    <test-detail-dialog
      :visible.sync="showDetailDialog"
      :test-data="currentTest"
    />

    <!-- 新建测试对话框 -->
    <create-test-dialog
      :visible.sync="showCreateDialog"
      :student-id="studentId"
      @success="handleCreateSuccess"
    />
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { mapActions } from 'vuex';
import TestDetailDialog from './TestDetailDialog.vue';
import CreateTestDialog from './CreateTestDialog.vue';

export default {
  name: 'TestScoresTab',
  components: {
    TestDetailDialog,
    CreateTestDialog,
  },
  props: {
    studentId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      chartPeriod: 'all',
      testStats: {},
      testRecords: [],
      trendChart: null,
      distributionChart: null,
      showDetailDialog: false,
      showCreateDialog: false,
      currentTest: null,
    };
  },
  created() {
    this.loadTestData();
  },
  mounted() {
    this.initCharts();
  },
  beforeDestroy() {
    if (this.trendChart) this.trendChart.dispose();
    if (this.distributionChart) this.distributionChart.dispose();
  },
  methods: {
    ...mapActions('student', ['fetchTestStats', 'fetchTestRecords', 'deleteTestRecord']),

    /**
     * 加载测试数据
     */
    async loadTestData() {
      this.loading = true;
      try {
        const params = {
          studentId: this.studentId,
          period: this.chartPeriod,
        };
        this.testStats = await this.fetchTestStats(params);
        this.testRecords = await this.fetchTestRecords(params);
        
        this.updateTrendChart();
        this.updateDistributionChart();
      } catch (error) {
        console.error('加载测试数据失败:', error);
        this.$message.error('加载测试数据失败');
      } finally {
        this.loading = false;
      }
    },

    /**
     * 周期改变
     */
    handlePeriodChange() {
      this.loadTestData();
    },

    /**
     * 新建测试
     */
    handleAddTest() {
      this.showCreateDialog = true;
    },

    /**
     * 查看详情
     */
    handleViewDetail(test) {
      this.currentTest = { ...test };
      this.showDetailDialog = true;
    },

    /**
     * 删除测试记录
     */
    handleDelete(test) {
      this.$confirm(`确定要删除测试"${test.testName}"吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        try {
          await this.deleteTestRecord(test.id);
          this.$message.success('删除成功');
          this.loadTestData();
        } catch (error) {
          console.error('删除测试记录失败:', error);
          this.$message.error('删除测试记录失败');
        }
      });
    },

    /**
     * 创建成功
     */
    handleCreateSuccess() {
      this.showCreateDialog = false;
      this.loadTestData();
    },

    /**
     * 初始化图表
     */
    initCharts() {
      this.trendChart = echarts.init(this.$refs.trendChart);
      this.distributionChart = echarts.init(this.$refs.distributionChart);
      
      this.updateTrendChart();
      this.updateDistributionChart();
    },

    /**
     * 更新趋势图
     */
    updateTrendChart() {
      if (!this.trendChart) return;

      const dates = this.testRecords.map((item) => item.testDate.split(' ')[0]);
      const scores = this.testRecords.map((item) => item.score);

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          formatter: (params) => {
            const param = params[0];
            return `${param.name}<br/>得分: ${param.value}%`;
          },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
        },
        yAxis: {
          type: 'value',
          name: '分数(%)',
          min: 0,
          max: 100,
        },
        series: [
          {
            name: '测试分数',
            type: 'line',
            data: scores,
            smooth: true,
            itemStyle: { color: '#409eff' },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(64, 158, 255, 0.5)' },
                { offset: 1, color: 'rgba(64, 158, 255, 0.1)' },
              ]),
            },
            markLine: {
              data: [
                { yAxis: 60, name: '及格线', lineStyle: { color: '#e6a23c', type: 'dashed' } },
              ],
            },
          },
        ],
      };

      this.trendChart.setOption(option);
    },

    /**
     * 更新分布图
     */
    updateDistributionChart() {
      if (!this.distributionChart) return;

      // 统计分数段分布
      const distribution = { excellent: 0, good: 0, pass: 0, fail: 0 };
      this.testRecords.forEach((test) => {
        if (test.score >= 90) distribution.excellent++;
        else if (test.score >= 80) distribution.good++;
        else if (test.score >= 60) distribution.pass++;
        else distribution.fail++;
      });

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} 次 ({d}%)',
        },
        legend: {
          bottom: 10,
        },
        series: [
          {
            name: '成绩分布',
            type: 'pie',
            radius: '60%',
            data: [
              { value: distribution.excellent, name: '优秀(90-100)', itemStyle: { color: '#67c23a' } },
              { value: distribution.good, name: '良好(80-89)', itemStyle: { color: '#409eff' } },
              { value: distribution.pass, name: '及格(60-79)', itemStyle: { color: '#e6a23c' } },
              { value: distribution.fail, name: '不及格(<60)', itemStyle: { color: '#f56c6c' } },
            ],
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
              },
            },
          },
        ],
      };

      this.distributionChart.setOption(option);
    },

    /**
     * 测试类型标签
     */
    testTypeTag(type) {
      const tagMap = {
        en_to_cn: 'success',
        cn_to_en: 'primary',
        listening: 'warning',
        spelling: 'info',
        daily: '',
        weekly: 'success',
        monthly: 'warning',
        final: 'danger',
      };
      return tagMap[type] || '';
    },

    /**
     * 测试类型文本
     */
    testTypeText(type) {
      const textMap = {
        en_to_cn: '英译中',
        cn_to_en: '中译英',
        listening: '听力',
        spelling: '拼写',
        daily: '日常测试',
        weekly: '周测',
        monthly: '月测',
        final: '期末测试',
      };
      return textMap[type] || '未知';
    },

    /**
     * 分数样式类
     */
    scoreClass(score) {
      if (score >= 90) return 'excellent-score';
      if (score >= 80) return 'good-score';
      if (score >= 60) return 'pass-score';
      return 'fail-score';
    },

    /**
     * 格式化时长 (秒转为可读格式)
     */
    formatDuration(seconds) {
      if (!seconds || seconds === 0) return '0秒';
      
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      
      if (minutes === 0) {
        return `${secs}秒`;
      } else if (secs === 0) {
        return `${minutes}分钟`;
      } else {
        return `${minutes}分${secs}秒`;
      }
    },
  },
};
</script>

<style scoped>
.test-scores-tab {
  padding: 10px 0;
}

.summary-row {
  margin-bottom: 20px;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.summary-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 24px;
}

.summary-content {
  flex: 1;
}

.summary-value {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 5px;
}

.summary-label {
  font-size: 13px;
  color: #909399;
}

.chart-card,
.detail-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  color: #303133;
}

.chart-container {
  height: 400px;
  width: 100%;
}

.success-text {
  color: #67c23a;
  font-weight: 600;
}

.excellent-score {
  color: #67c23a;
  font-weight: 600;
  font-size: 16px;
}

.good-score {
  color: #409eff;
  font-weight: 600;
  font-size: 16px;
}

.pass-score {
  color: #e6a23c;
  font-weight: 600;
  font-size: 16px;
}

.fail-score {
  color: #f56c6c;
  font-weight: 600;
  font-size: 16px;
}
</style>
